"use client";
import { PropsWithChildren, useContext, useEffect } from "react";
import { createContext } from "react";
import { useParams } from "next/navigation";
import { useSessionStorage } from "@mantine/hooks";
import { useParsedQueryParams, type ParsedQueryParams } from "@/screens/Welcome/useQueryParams";
import { sessionStorageKeys } from "@/constants/common";

export type QueryStorageContextState = {
  storedQueries: ParsedQueryParams;
  setStoredQueries: (queries: ParsedQueryParams) => void;
  addQuery: (key: string, query: unknown) => void;
  resetStoredQueries: () => void;
};

const QueryStorageContext = createContext<QueryStorageContextState>({
  storedQueries: {} as ParsedQueryParams,
  setStoredQueries: () => {},
  addQuery: () => {},
  resetStoredQueries: () => {},
});

export function useQueryStorage() {
  return useContext(QueryStorageContext);
}

type PersistedQueryStorage = { uuid: string; queries: ParsedQueryParams };

export const QueryStorageContextWrapper = ({ children }: PropsWithChildren) => {
  const params = useParams();
  const uuid = params.id ? String(params.id) : "";
  const parsedQueries = useParsedQueryParams();

  const [persisted, setPersisted] = useSessionStorage<PersistedQueryStorage>({
    key: sessionStorageKeys.storedQueries,
    defaultValue: { uuid, queries: parsedQueries },
    getInitialValueInEffect: false,
  });

  // Use the persisted queries while we stay on the same request, otherwise trust the URL. The `uuid`
  // check keeps the id-less "new"/"draft" welcome routes on their own URL instead of stale persisted data.
  const storedQueries = uuid && persisted.uuid === uuid ? persisted.queries : parsedQueries;

  // Tie the persisted record to the active request: carry the queries while the request continues (incl.
  // the transition from a "new"/"draft" route that has no id yet), reset to the URL for a different request.
  useEffect(() => {
    setPersisted(current => {
      const continues = current.uuid === uuid || current.uuid === "";
      return { uuid, queries: continues ? current.queries : parsedQueries };
    });
  }, [uuid]);

  const setStoredQueries = (queries: ParsedQueryParams) => {
    setPersisted({ uuid, queries });
  };

  const addQuery = (key: string, query: unknown) => {
    setPersisted({ uuid, queries: { ...storedQueries, [key]: query } });
  };

  const resetStoredQueries = () => {
    setPersisted({ uuid, queries: {} as ParsedQueryParams });
  };

  return (
    <QueryStorageContext.Provider value={{ storedQueries, setStoredQueries, addQuery, resetStoredQueries }}>
      {children}
    </QueryStorageContext.Provider>
  );
};
